import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { WarningIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { EmbeddedMedia } from '#src/app/shared/components/embeddedMedia';

import type { MediaHitEmbed } from '../../../shared/context';
import { useMediaHitsPermissions } from '../../../shared/hooks/useMediaHitsPermissions';
import { DeleteMediaHit } from '../../DeleteMediaHit';
import { LoadingSkeleton } from '../../LoadingSkeleton';

const CARD_CLASS_NAME = 'w-[300px] shrink-0 snap-start scroll-ml-6 md:w-[500px] md:snap-align-none md:scroll-ml-0';

type MediaHitProps = {
	media: MediaHitEmbed;
	removeMedia: (mediaId: string) => void;
};

const InvalidMediaHitContent: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	const { translate } = useI18n();

	return (
		<>
			<div className="mt-[42px] flex h-[110px] rounded-lg bg-[#403a490d] p-4 text-black md:h-[156px]">
				<WarningIcon size="small" className="text-icon-warning mt-0.5 mr-2 shrink-0" />
				<div className="flex h-full min-w-0 flex-1 flex-col justify-between overflow-hidden">
					<Text variant="heading" size="xx-small" truncate>
						{translate('corgi.petition_gamma.media_mentions.invalid.title')}
					</Text>
					<Text as="p" size="small" truncate>
						<Translate value="corgi.petition_gamma.media_mentions.invalid.message" />
					</Text>
					<Text as="p" size="small" className="line-clamp-2 text-wrap">
						URL: {media.url}
					</Text>
				</div>
			</div>
			<div className="my-2">
				<DeleteMediaHit media={media} removeMedia={removeMedia} />
			</div>
		</>
	);
};

const ValidMediaHitContent: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	return (
		<div className="mt-10.5">
			<EmbeddedMedia html={media.html} aspectRatio={16 / 9} />
			<div className="my-2">
				<DeleteMediaHit media={media} removeMedia={removeMedia} />
			</div>
		</div>
	);
};

export const MediaHit: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	// Only show invalid media if the user can delete it. Once an embed has resolved invalid and the user can't
	// delete it, the card collapses out of the carousel entirely.
	if (media.status === 'invalid' && !canDelete) {
		return null;
	}

	return (
		<div data-testid={`media-hit-${media.id}`} className={CARD_CLASS_NAME}>
			{media.status === 'loading' && <LoadingSkeleton />}
			{media.status === 'valid' && <ValidMediaHitContent media={media} removeMedia={removeMedia} />}
			{media.status === 'invalid' && <InvalidMediaHitContent media={media} removeMedia={removeMedia} />}
		</div>
	);
};
